// Import react elements
import { useCallback, useContext, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from 'react-router-dom';

// Import core functions
import { getTransactions, getTransactionsByDateRange, getPayouts, getPayout, requestPayout, updatePayPalPayoutEmail, getUserAccountData } from "api/APIStrichCalls";

// Import context function
import { UserContext } from "global/context/UserContext";
import { AccountLastUpdatedContext } from "global/context/AccountLastUpdatedContext";

// Import hooks
import {fetchOrder} from "pages/orders/components/Orders";
import { useToast } from "global/hooks/useToast";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import CreateButton from "global/components/CreateButton";
import { useTableFilters } from "global/components/CustomisableTable";
import OrderDetailsPopUp from "pages/orders/components/OrderDetailsPopUp";
import EarningBreakdown from "global/components/EarningBreakdown";

// Import icons
import {CloseIcon} from "global/strich_icons/StrichIcons"

// Import the transactions and payouts tables
import TransactionsTable from "./TransactionsTable";
import PayoutsTable from "./PayoutsTable";

// Import utilities
import { FormatCurrency } from "global/utilities/ConvertCurrency";
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import { formatDisplayDateTime } from "global/utilities/ConvertDate";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";
import RefundBreakdown from "global/components/RefundBreakdown";


// Format the time remaining until a transaction becomes available
const formatAvailableIn = (availableDate) => {

    // Return nothing when there is no availability date
    if (!availableDate) return "";

    // Parse API dates using the same UTC convention as the normal date formatter
    const utcString = String(availableDate).replace(" ", "T").replace(/Z?$/, "Z");
    const availableTime = new Date(utcString).getTime();

    // Return nothing when the supplied date is invalid
    if (!Number.isFinite(availableTime)) return "";

    // Calculate how long remains until the transaction becomes available
    const millisecondsRemaining = availableTime - Date.now();

    // Transactions whose release time has passed are available now
    if (millisecondsRemaining <= 0) return "Now";

    // Show minutes when less than one hour remains
    const minutesRemaining = Math.ceil(millisecondsRemaining / 60000);
    if (minutesRemaining < 60) {
        if (minutesRemaining === 1) return "1 minute";
        return minutesRemaining + " minutes";
    }

    // Show hours when less than one day remains
    const hoursRemaining = Math.ceil(millisecondsRemaining / 3600000);
    if (hoursRemaining < 24) {
        if (hoursRemaining === 1) return "1 hour";
        return hoursRemaining + " hours";
    }

    // Show days for longer availability periods
    const daysRemaining = Math.ceil(millisecondsRemaining / 86400000);
    if (daysRemaining === 1) return "1 day";
    return daysRemaining + " days";

};


// Format the columns for the transactions table
const initialTableColumns = [

    // Select tick box at left of table rows - if we want to multi select transactions and action them
    //{ key: 'select', label: '', resizable: false, minWidth: 40, maxWidth: 40, draggable: false },

    // View button to see more details for that row
    //{ key: 'view', label: '', resizable: false, minWidth: 50, maxWidth: 50, draggable: false },

    { key: 'transaction_date', label: 'Date', resizable: true, minWidth: 120, maxWidth: 300, draggable: true, textFilter: false },
    { key: 'type', label: 'Type', resizable: true, minWidth: 138, maxWidth: 400, draggable: true, textFilter: false },
    { key: 'order_id', label: 'Order ID', resizable: true, minWidth: 138, maxWidth: 400, draggable: true, textFilter: false },
    { key: 'id', label: 'Reference', resizable: true, minWidth: 110, maxWidth: 600, draggable: true, textFilter: false },
    { key: 'transactionAmount', label: 'Amount', resizable: true, minWidth: 90, maxWidth: 180, draggable: true, textFilter: false, td_classname: "alight-to-right" },
    { key: 'availableIn', label: 'Available In', resizable: true, minWidth: 110, maxWidth: 180, draggable: true, textFilter: false },
    { key: 'notes', label: 'Notes', resizable: true, minWidth: 110, maxWidth: 600, draggable: true, textFilter: false },

];



// Format the columns for the payouts table
const initialPayoutTableColumns = [

    { key: 'request_date', label: 'Requested', resizable: true, minWidth: 140, maxWidth: 300, draggable: true, textFilter: false },
    { key: 'paid_date', label: 'Paid', resizable: true, minWidth: 140, maxWidth: 300, draggable: true, textFilter: false },
    { key: 'method', label: 'Method', resizable: true, minWidth: 100, maxWidth: 220, draggable: true, textFilter: false },
    { key: 'destination', label: 'Destination', resizable: true, minWidth: 180, maxWidth: 500, draggable: true, textFilter: false },
    { key: 'status', label: 'Status', resizable: true, minWidth: 100, maxWidth: 220, draggable: true, textFilter: false },
    { key: 'payoutAmount', label: 'Amount', resizable: true, minWidth: 80, maxWidth: 150, draggable: true, textFilter: false, td_classname: "alight-to-right" },

];



const Transactions = (token, authReady) => {

    // Extract the connections data and shipping methods from the user context - allows us to use their data anywhere inside this component without needing to manually pass it down as a prop
    // Added refresh user data to this so we can update the available balance after requesting a payout
    const { userData, refreshUserData } = useContext(UserContext);

    // Get the available balance returned by the account API
    let availableBalance = 0;
    let hasAvailableBalance = false;
    if (userData?.available_balance !== null && userData?.available_balance !== undefined && Number.isFinite(Number(userData.available_balance))) {
        availableBalance = Number(userData.available_balance);
        hasAvailableBalance = true;
    }

    // Only allow payouts when there is a positive available balance
    const canRequestPayout = hasAvailableBalance && availableBalance > 0;

    // Set the payout button tooltip
    let payoutButtonTooltip = "No earnings to payout";
    if (canRequestPayout) { payoutButtonTooltip = "Request Payout"; }

    // Get the global account update markers
    const { lastUpdated } = useContext(AccountLastUpdatedContext);

    // Get the transactions marker
    const transactionsLastUpdated = lastUpdated?.transactions_last_updated || null;

    // Navigate function to go to other pages
    const navigate = useNavigate();

    // Soldiers. Soldiers, toast? Geddit?
    const { toastSuccess, toastError } = useToast();

    // Set state for holding the payouts details
    const [payoutsData, setPayoutsData] = useState([]);

    // Set loading state - while api call is fetching payouts
    const [loadingPayouts, setLoadingPayouts] = useState(true);

    // Set loading state - while api call is creating a payout
    const [creatingPayout, setCreatingPayout] = useState(false);

    // Set state for holding the transactions details
    const [transactionsData, setTransactionsData] = useState([]);

    // Set loading state - while api call is fetching transactions
    const [loadingTransactions, setLoadingTransactions] = useState(true);

    // Set popup state - for displaying earning details
    const [displayTransactionPopup, setDisplayTransactionPopup] = useState(false);

    // Set state for holding the selected transaction details
    const [selectedTransaction, setSelectedTranscation] = useState(null);
    
    // Set popup state - for user to add payout email
    const [displayAddPayoutEmailPopup, setDisplayAddPayoutEmailPopup] = useState(false);

    // Track button loading state
    const [updatingPayPalPayoutEmail, setUpdatingPayPalPayoutEmail] = useState(false);

    // Set state for holding the payout details
    const [payoutDetails, setPayoutDetails] = useState(null);

    // Set popup state - for displaying pay out details
    const [displayPayoutPopup, setDisplayPayoutPopup] = useState(false);

    // Set loading state - for displaying pay out details
    const [loadingPayoutDetails, setLoadingPayoutDetails] = useState(false);

    // Set pop up state for the orders details pop up
    const [orderDetailsPopupOpen, setOrderDetailsPopupOpen] = useState(false);

    // Set loading state - while api call is fetching order
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);

    // Set empty array state for selected order
    const [orderDetails, setOrderDetails] = useState([]);

    // Store the latest global transactions marker without forcing fetch functions to depend on it
    const transactionsLastUpdatedRef = useRef(null);

    // Store the marker that was current when transactions were last loaded
    const loadedTransactionsLastUpdatedRef = useRef(null);

    // Prevent overlapping automatic refreshes
    const autoRefreshInProgressRef = useRef(false);


    // Keep the transactions marker ref current
    useEffect(() => {

        // Store the latest marker
        transactionsLastUpdatedRef.current = transactionsLastUpdated;

    }, [transactionsLastUpdated]);


    // Render the transaction amount with conversion information when available
    const renderTransactionAmount = (transaction) => {

        // Format the transaction amount in the transaction currency
        const transactionAmount = FormatCurrency(transaction.currency, transaction.amount, true);

        return transactionAmount;

    };



    // Render the transaction order id with link to open th eoder in a pop up
    const renderTransactionOrderId = (transaction) => {

        // Get order id safely
        const transactionOrderId = transaction.order_id || null;

        // Return empty span if there's no order id
        if (!transactionOrderId) return null;

        // Return the order id with link to pop up
        return (

            <span 
                className="transaction_order-id navigate-text-link" 
                title="View Order Details"
                onClick={async (e)  => {

                    e.stopPropagation();

                    // Set flag to say that it's only looking for an order id - so we can customizr error message on empty result
                    const searchOrderIdOnly = true;

                    // Fetch the order details
                    await fetchOrder(transaction.order_id, setLoadingOrderDetails, setOrderDetails, setOrderDetailsPopupOpen, toastError, token, searchOrderIdOnly);

                }} 
            >
                <span>{transaction.order_id}</span>
            </span>

        );

    };



    // Make call to get transactions
    // Use useCallback to create a stable fetchTransactions function that depends on token and authReady
    const fetchTransactions = useCallback(async (quietRefresh = false) => {

        // Check if there's no token or the authReady flag is false
        if (!authReady || !token)

            // Exit early
            return;

        // Set loading to true unless this is a quiet background refresh
        if (!quietRefresh) { setLoadingTransactions(true); }

        try {

            // Get api response
            const response = await getTransactions(token);

            // Check if it is successful and that the results is an array
            if (response.success && Array.isArray(response.results)) {

                // It is so map the transactions returned
                const mappedTransactions = response.results.map((transaction) => {

                    // Create an array with formatted data that we want to display in the earnings table
                    return {

                        ...transaction,

                        // Format the amount and add conversion details when available
                        transactionAmount: renderTransactionAmount(transaction),
                        order_id: renderTransactionOrderId(transaction),
                        transaction_date: formatDisplayDateTime(transaction.transaction_date),
                        availableIn: formatAvailableIn(transaction.available_date),

                    };

                });

                // Update transactionsData with the response results
                //setTransactionsData(response.results);
                setTransactionsData(mappedTransactions);

                // Remember the marker this transaction load was based on
                loadedTransactionsLastUpdatedRef.current = transactionsLastUpdatedRef.current;

            }

        } catch (err) {

            // Log the error to the console for debugging
            console.error("Transaction API error:", err);

        } finally {

            // Set loading state to false once the API call is complete unless this is a quiet background refresh
            if (!quietRefresh) { setLoadingTransactions(false); }

        }

        // Dependencies that trigger a new instance of this function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authReady, token]);



    // Make call to get payouts
    // Use useCallback to create a stable fetchPayouts function that depends on token and authReady
    const fetchPayouts = useCallback(async (quietRefresh = false) => {

        // Check if there's no token or the authReady flag is false
        if (!authReady || !token)

            // Exit early
            return;

        // Set loading to true unless this is a quiet background refresh
        if (!quietRefresh) { setLoadingPayouts(true); }

        try {

            // Get api response
            const response = await getPayouts(token);

            // Check if it is successful and that the results is an array
            if (response.success && Array.isArray(response.results)) {

                // Map the payouts returned
                const mappedPayouts = response.results.map((payout) => {

                    return {

                        ...payout,
                        payoutAmount: FormatCurrency(userData?.currency, payout.amount, true),
                        request_date: formatDisplayDateTime(payout.request_date),
                        paid_date: formatDisplayDateTime(payout.paid_date),

                    };

                });

                // Update payoutsData with the response results
                setPayoutsData(mappedPayouts);

            } else {

                setPayoutsData([]);

            }

        } catch (err) {

            // Log the error to the console for debugging
            console.error("Payout API error:", err);
            setPayoutsData([]);

        } finally {

            // Set loading state to false once the API call is complete unless this is a quiet background refresh
            if (!quietRefresh) { setLoadingPayouts(false); }

        }

    }, [authReady, token, userData?.currency]);



    // useEffect to automatically fetch transactions when the component mounts or when dependencies update
    useEffect(() => {

        // Call fetchTransactions and fetchPayouts on component mount or when token/authReady change
        fetchTransactions();
        fetchPayouts();

        // Dependencies that trigger a new instance of this function
    }, [fetchTransactions, fetchPayouts]);



    // Refresh transactions and payouts when the global transactions marker changes
    useEffect(() => {

        // Only refresh when auth is ready
        if (!authReady || !token) { return; }

        // Ignore empty/default markers
        if (!transactionsLastUpdated || transactionsLastUpdated === "0001-01-01 00:00:00") { return; }

        // If this is the first marker seen, store it without refreshing
        if (!loadedTransactionsLastUpdatedRef.current) {
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdated;
            return;
        }

        // Stop if the marker has not changed since the last transaction load
        if (transactionsLastUpdated === loadedTransactionsLastUpdatedRef.current) { return; }

        // Stop if an automatic refresh is already running
        if (autoRefreshInProgressRef.current) { return; }

        // Mark the automatic refresh as running
        autoRefreshInProgressRef.current = true;

        // Reload transactions and payouts quietly in the background
        Promise.all([
            fetchTransactions(true),
            fetchPayouts(true),
            refreshUserData()
        ]).finally(() => {

            // Mark the automatic refresh as finished
            autoRefreshInProgressRef.current = false;

        });

    }, [authReady, token, transactionsLastUpdated, fetchTransactions, fetchPayouts, refreshUserData]);



    // Funtion to fetch transactions by a specified date range
    const fetchTransactionsByDateRange = async (fromDate, toDate) => {

        // Set loading state
        setLoadingTransactions(true);

        // Make the call
        const transactionsByDateRangeResponse = await getTransactionsByDateRange(token, fromDate, toDate);

        // Check if it is successful
        if (transactionsByDateRangeResponse.success) {

            // It is so map the transactions returned
            const mappedTransactions = transactionsByDateRangeResponse.results.map((transaction) => {

                // Create an array with formatted data that we want to display in the orders table
                return {

                    ...transaction,
  
                    // Format the amount and add conversion details when available
                    transactionAmount: renderTransactionAmount(transaction),
                    order_id: renderTransactionOrderId(transaction),
                    transaction_date: formatDisplayDateTime(transaction.transaction_date),
                    availableIn: formatAvailableIn(transaction.available_date),

                };

            });

            // Update transactionsData with the formatted response
            setTransactionsData(mappedTransactions);

            // Clear loading state 
            setLoadingTransactions(false);

        } else {

            // Clear loading state 
            setLoadingTransactions(false);

            // Toast fail
            toastError({
                title: "Error",
                message: "Could not fetch earnings: " + transactionsByDateRangeResponse.error_message,
                autoCloseMs: 6000
            });

        }

    }



    // This defines which filters the main transactions table supports
    const transactionFilterKeys = useMemo(() => ["type"], []);


    // This defines which filters the payout transactions table supports
    const payoutTransactionFilterKeys = useMemo(() => ["type"], []);


    // This defines which filters the payouts table supports
    const payoutFilterKeys = useMemo(() => ["method", "status"], []);


    // This creates filters for the main transactions table
    const transactionFilters = useTableFilters(transactionsData, transactionFilterKeys);


    // This creates filters for the payouts table
    const payoutFilters = useTableFilters(payoutsData, payoutFilterKeys);


    // This creates filters for the payout popup transactions table
    const payoutTransactionFilters = useTableFilters(payoutDetails?.transactions || [], payoutTransactionFilterKeys);



    // Handle clicking a earning row
    const handleSelectTransaction = async (transactionId) => {

        // Clear the state
        setSelectedTranscation(null);

        // Open the pop up
        setDisplayTransactionPopup(true);
        
        // Get the earning
        const selectedEarning = transactionsData.find((transaction) => transaction.id === transactionId);   

        // Update the state
        setSelectedTranscation(selectedEarning);

    };



    // Handle clicking a payout row
    const handleSelectPayout = async (payout_id) => {

        // Set loading
        setLoadingPayoutDetails(true);

        // Make the call
        const response = await getPayout(token, payout_id);

        // Check success
        if (response.success) {

            // Get the results safely
            const safePayout = response.results || {};

            // Map the transactions within the payout - to format the amount value and order id link
            const mappedPayout = {

                ...safePayout,

                transactions: safePayout.transactions.map((transaction) => {

                    return {

                        ...transaction,

                        transaction_date: formatDisplayDateTime(transaction.transaction_date),
                        availableIn: formatAvailableIn(transaction.available_date),

                        // Format the amount and add conversion details when available
                        transactionAmount: renderTransactionAmount(transaction),
                        
                        // Format the order id so it's a clickable link
                        order_id: renderTransactionOrderId(transaction),

                    }

                })

            };

            // Update the state
            setPayoutDetails(mappedPayout);

            // Open the pop up
            setDisplayPayoutPopup(true);

            // Clear loading
            setLoadingPayoutDetails(false);

        } else {

            // Clear loading
            setLoadingPayoutDetails(false);
 
            // Toast error
            toastError({ title: "Error", message: "Could not load payout details.", apiErrorMessage: response.error_message,  autoCloseMs: 6000 });

        }
       
    };



    // Handle requesting a payout
    const handleRequestPayout = async (newPaypalPayoutEmail) => {

        // Check if the user has added a payout email
        if (!userData.paypal_payout_email && !newPaypalPayoutEmail) {

            // Open the pop up so they can add a payout email or view their account
            setDisplayAddPayoutEmailPopup(true);

        } else {

            // Display confirmation
            DisplayConfirmationDialog(
    
                `<h3>Are you sure?</h3> <br/> Request payout of ${FormatCurrency(userData?.currency, availableBalance, true)} <br/><br/><span class='warning-message'>WARNING: Payout requests cannot be cancelled or reversed once submitted. You may only submit one payout request every 24 hours.</span>`,
                "Request Payout",
                "Cancel",
                async function (is_confirmed) {
    
                    // If user confirms, proceed 
                    if (is_confirmed) {
    
                        // Set loading state
                        setCreatingPayout(true);

                        try {

                            // Make the call
                            const response = await requestPayout(token, "paypal");

                            if (!response?.success) {

                                // Toast error 
                                toastError({
                                    title: "Error",
                                    message: response?.error_message || "Could not request payout.",
                                    autoCloseMs: 6000
                                });

                                return;

                            }

                            // Toast success
                            toastSuccess({ title: "Success", message: "Payout requested successfully." });

                            // Refetch data so UI updates immediately
                            fetchPayouts();
                            fetchTransactions();
                            refreshUserData();

                        } catch (error) {

                            // Toast error
                            toastError({
                                title: "Error",
                                message: "Could not request payout.",
                                autoCloseMs: 6000
                            });

                        } finally {

                            // Clear loading state
                            setCreatingPayout(false);

                        }
    
                    }
    
                }
    
            );

        }

    };



    // Function to update a user's paypal payout email
    const handleUpdatePayoutEmail = async (event) => {

        // Stop the page refreshing when the form submits
        event.preventDefault();

        // Get the submitted form data
        const formData = new FormData(event.currentTarget);

        // Get the PayPal payout email from the input name
        const newPayPalPayoutEmail = formData.get("paypal_payout_email");

        // Set loading state while the request runs
        setUpdatingPayPalPayoutEmail(true);
     
        // Send the updated PayPal payout email to the API
        const response = await updatePayPalPayoutEmail(token, newPayPalPayoutEmail);

        // Check whether the update succeeded
        if (response?.success) {

            // Refresh the user data so the page reflects the saved value
            await refreshUserData();

            // Get the new user account data
            const accountDataResponse = await getUserAccountData(token);

            // Make the payout - send the newly added payout email address
            await handleRequestPayout(accountDataResponse.results.paypal_payout_email);

            // Clear loading state
            setUpdatingPayPalPayoutEmail(false);

            // Close the pop up
            setDisplayAddPayoutEmailPopup(false);

        } else {
            
            // Clear loading state
            setUpdatingPayPalPayoutEmail(false);

            // Create error message
            const errorMessage = response.error_message ? response.error_message : "Oops...something went wrong."

            // Toast error
            toastError({title: "Error", message: "Could not add PayPal payout email: " + errorMessage, autoCloseMs: 6000});

        }

    }



    return (

        <>
            <Grid>

                <GridItem>

                    <h3>Balance Affecting Transactions</h3>

                    <br />

                    <TransactionsTable
                        initialTableColumns={initialTableColumns}
                        transactionsData={transactionsData}
                        loadingTransactions={loadingTransactions}
                        uniqueFilterOptions={transactionFilters.filterOptions}
                        defaultFilters={transactionFilters.defaultFilters}
                        fetchTransactionsByDateRange={fetchTransactionsByDateRange}
                        handleSelectTransaction={handleSelectTransaction}
                        rowKeyPrefix="main-transactions"
                    />

                </GridItem>

            </Grid>

            <Grid>

                <GridItem>

                    <h3>Payouts</h3>
                   
                    <PayoutsTable
                        initialTableColumns={initialPayoutTableColumns}
                        payoutsData={payoutsData}
                        loadingPayouts={loadingPayouts}
                        uniqueFilterOptions={payoutFilters.filterOptions}
                        defaultFilters={payoutFilters.defaultFilters}
                        handleRowSingleClick={handleSelectPayout}
                    />

                    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>

                        <p style={{ margin: 0, whiteSpace: "nowrap" }}>

                            <strong>Available Earnings:</strong> 

                            {userData?.currency && hasAvailableBalance ? 

                                <> {FormatCurrency(userData?.currency, availableBalance, true)}</>

                                :

                                <span className="warning-message"> Not available</span>

                            }

                        </p>
                       
                        <CreateButton
                            title={creatingPayout ? "Requesting..." : "Request Payout"}
                            size="s"
                            type="button"
                            tooltip={payoutButtonTooltip}
                            onClick={() => handleRequestPayout()}
                            disabled={creatingPayout || !canRequestPayout}
                        />

                    </div>

                </GridItem>

            </Grid>
          

            {/* Display pop up if there's no payout email address for the account */}
            {displayAddPayoutEmailPopup &&
            
                <div className="popup-overlay">
                
                    <div className="popup-container popup-container--small">
        
                            {/* This is the modal header */}
                        <div className="popup__header">
        
                            {/* This is the title area */}
                            <div className="popup__headerLeft">
        
                                {/* This is the parent title */}
                                <h2 className="popup__title">
        
                                    Add PayPal Payout Email
        
                                </h2>
        
                                {/* This is the parent subtitle */}
                                <p className="popup__subtitle">
        
        
                                </p>
        
                            </div>
        
                            {/* This is the header actions */}
                            <div className="popup__headerRight">
        
                                {/* This is the close button */}
                                <button type="button" className="popup__close" onClick={() => setDisplayAddPayoutEmailPopup(false)} title="Close">
                                    <CloseIcon />
                                </button>
        
                            </div>
        
                        </div>
        
                                        
                        <div className="popup-content popup-content__body">

                            <div className="add-payout-email">

                                <p>Oops...looks like you haven't added a payout email address to your account.</p>

                                <br/>

                                <p>Enter a valid PayPal email address below to request a payout or go to your <span className="navigate-text-link" onClick={() => navigate("/account")}>account page</span> to view your current settings.</p>

                                <br/>

                                <form id="payoutEmailForm" onSubmit={handleUpdatePayoutEmail}>

                                    <div>

                                        <input
                                            name="paypal_payout_email"
                                            type="text"
                                            disabled={updatingPayPalPayoutEmail}
                                            placeholder="PayPal Payout Email..."
                                            className="transactions_paypal_payout_email"
                                        />

                                    </div>
                                
                                </form>

                                <br/>

                                <p>When you click 'submit and request' the PayPal payout email will be added to your account to use for future payouts. The payout request will then be submitted to the email provided.</p>
                          
                                <br/>
                                <br/>

                                <p className="warning-text">Please note that payment requests can only be made once every 24 hours.</p>

                            </div>

                        </div>

                        {/* Footer actions */}
                        <div className="tabs-footer tabs-footer--no-background horizontal-align-content-center flex-justify-left-right">

                            <div>

                            </div>

                            <div className="horizontal-align-content-center">

                                <CreateButton type="submit" formID="payoutEmailForm" title={updatingPayPalPayoutEmail ? "Requesting payout..." : "Submit & Request Payout"} disabled={updatingPayPalPayoutEmail} />
                                
                                <CreateButton title="Cancel" isCancel={true} invert size="m" onClick={() => setDisplayAddPayoutEmailPopup(false)} disabled={updatingPayPalPayoutEmail} />

                            </div>

                        </div>

                    </div>

                </div>

            }


            {/* Display payout details pop up */}
            {displayPayoutPopup &&
                        
                <div className="popup-overlay">
                
                    <div className="popup-container popup-container--medium">
        
                            {/* This is the modal header */}
                        <div className="popup__header">
        
                            {/* This is the title area */}
                            <div className="popup__headerLeft">
        
                                {/* This is the parent title */}
                                <h2 className="popup__title">
        
                                    Payout Details
        
                                </h2>
        
                                {/* This is the parent subtitle */}
                                <p className="popup__subtitle">
        
        
                                </p>
        
                            </div>
        
                            {/* This is the header actions */}
                            <div className="popup__headerRight">
        
                                {/* This is the close button */}
                                <button type="button" className="popup__close" onClick={() => setDisplayPayoutPopup(false)} title="Close">
                                    <CloseIcon />
                                </button>
        
                            </div>
        
                        </div>

                        {loadingPayoutDetails ? 
                        
                            <LoadingSpinner/>

                            :

                            <div className="popup-content popup-content__body">

                                <Grid>
                                    <GridItem>

                                        <div className="payout-fieldRow">
                                            <label className="fieldRow__label">Amount:</label>
                                            <span className="fieldRow__textarea">{FormatCurrency(userData?.currency, payoutDetails?.amount || 0, true)}</span>
                                        </div>   

                                        <div className="payout-fieldRow">
                                            <label className="fieldRow__label">Requested Date:</label>
                                            <span className="fieldRow__textarea">{formatDisplayDateTime(payoutDetails.request_date)}</span>
                                        </div>   

                                        <div className="payout-fieldRow">
                                            <label className="fieldRow__label">Paid Date:</label>
                                            <span className="fieldRow__textarea">{formatDisplayDateTime(payoutDetails.paid_date)}</span>
                                        </div>   

                                        <div className="payout-fieldRow">
                                            <label className="fieldRow__label">Status:</label>
                                            <span className="fieldRow__textarea">{payoutDetails.status}</span>
                                        </div>   

                                        <div className="payout-fieldRow">
                                            <label className="fieldRow__label">Method:</label>
                                            <span className="fieldRow__textarea">{payoutDetails.method}</span>
                                        </div> 

                                        <div className="payout-fieldRow payout-fieldRow-collapsable"> 
                                            <label className="fieldRow__label">Payout Email:</label>
                                            <span className="fieldRow__textarea">{payoutDetails.destination}</span>
                                        </div>  
                                        
                                    </GridItem>

                                    

                                </Grid>

                                <Grid>

                                    <GridItem>

                                        <h3>Transactions</h3>

                                        <TransactionsTable
                                            initialTableColumns={initialTableColumns}
                                            transactionsData={payoutDetails.transactions}
                                            loadingTransactions={loadingTransactions}
                                            uniqueFilterOptions={payoutTransactionFilters.filterOptions}
                                            defaultFilters={payoutTransactionFilters.defaultFilters}
                                            //fetchTransactionsByDateRange={fetchTransactionsByDateRange}
                                            rowKeyPrefix="payout-transactions"
                                            handleSelectTransaction={handleSelectTransaction}
                                            displayDatePicker={false}
                                        />

                                    </GridItem>

                                </Grid>

                            </div>
                    
                        }
        
                    </div>

                </div>


            }


            {/* Display transaction details pop up */}
            {displayTransactionPopup &&
                        
                <div className="popup-overlay">
                
                    <div className="popup-container popup-container--medium">
        
                            {/* This is the modal header */}
                        <div className="popup__header">
        
                            {/* This is the title area */}
                            <div className="popup__headerLeft">
        
                                {/* This is the parent title */}
                                <h2 className="popup__title">
        
                                    {selectedTransaction.type ? selectedTransaction.type : "Transaction" } Details
        
                                </h2>
        
                                {/* This is the parent subtitle */}
                                <p className="popup__subtitle">
        
        
                                </p>
        
                            </div>
        
                            {/* This is the header actions */}
                            <div className="popup__headerRight">
        
                                {/* This is the close button */}
                                <button type="button" className="popup__close" onClick={() => {setDisplayTransactionPopup(false); setSelectedTranscation(null)}} title="Close">
                                    <CloseIcon />
                                </button>
        
                            </div>
        
                        </div>

                        <div className="popup-content popup-content__body">

                            <div className="earning-details">
                                
                                <div className="earning-details__grid">

                                    <div className="earning-details__row">

                                        <span className="earning-details__label">Type</span>

                                        <span className="earning-details__value">{selectedTransaction.type}</span>

                                    </div>

                                    <div className="earning-details__row">

                                        <span className="earning-details__label">Amount</span>

                                        <span className="earning-details__value">{FormatCurrency(selectedTransaction.currency, selectedTransaction?.amount || 0, true)}</span>

                                    </div>

                                    <div className="earning-details__row">

                                        <span className="earning-details__label">Date</span>

                                        <span className="earning-details__value">{selectedTransaction.transaction_date}</span>

                                    </div>

                                    <div className="earning-details__row">

                                        <span className="earning-details__label">Available Date</span>

                                        <span className="earning-details__value">{formatDisplayDateTime(selectedTransaction.available_date)}</span>

                                    </div>

                                    {selectedTransaction.order_id &&

                                        <div className="earning-details__row">

                                            <span className="earning-details__label">Related Order ID</span>

                                            <span className="earning-details__value">{selectedTransaction.order_id}</span>

                                        </div>

                                    }
                                
                                    <div className="earning-details__row">

                                        <span className="earning-details__label">Reference</span>

                                        <span className="earning-details__value">{selectedTransaction.id || "—"}</span>

                                    </div>

                                    

                                    <div className="earning-details__row earning-details__row--full">

                                        <span className="earning-details__label">Notes</span>

                                        <span className="earning-details__value">{selectedTransaction.notes || "-"}</span>

                                    </div>

                                </div>

                                {selectedTransaction.transaction_data?.earning_breakdown &&

                                    <div className="earning-details__breakdown">

                                        <EarningBreakdown earningBreakdown={selectedTransaction?.transaction_data?.earning_breakdown} />

                                    </div>

                                }

                                {selectedTransaction.transaction_data?.refund_breakdown &&

                                    <div className="earning-details__breakdown">

                                        <RefundBreakdown refundBreakdown={selectedTransaction?.transaction_data?.refund_breakdown} />

                                    </div>

                                }
                                                                        
                            </div>

                        </div>
        
                    </div>

                </div>

            }

            
            {/* Display loading screen if fetching order details */}
            {loadingOrderDetails &&
            
                <LoadingScreen loadingMessage="Loading Order Details..."/>

            }


            {/* Display order details pop up */}
            {orderDetailsPopupOpen &&
            
                <OrderDetailsPopUp
                    isOpen={orderDetailsPopupOpen}
                    onClose={() => {
                        setOrderDetailsPopupOpen(false);
                    }}
                    loadingOrderDetails={loadingOrderDetails}
                    orderDetails={orderDetails}
                    addingNewOrder={false}
                    setAddingNewOrder={null}
                />
            
            }


        </>
    );

};

export default Transactions;